import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  Zap,
  FileSearch,
  FileText,
  BellRing,
  TrendingUp,
  Globe,
  Download,
  Plug,
  CheckCircle2,
  BookOpen,
  ExternalLink,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomerLogos from '@/components/CustomerLogos';
import reportDashboard from '@/assets/product-report-atlanta-dashboard.png';

const Product = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Fordje',
    description:
      'A live requirements platform for solar, storage, and EV projects. Every requirement mapped, cited, and kept current from feasibility through inspection.',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    provider: { '@type': 'Organization', name: 'Fordje', url: 'https://www.fordje.com' },
    url: 'https://www.fordje.com/product',
  };


  const faqData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What does the Fordje product do?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Fordje maps every requirement that applies to a clean energy project — design, engineering, permitting, interconnection, and inspection — cites each one to its source document, and keeps the record current as rules change.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the difference between a Feasibility Report and a Comprehensive Report?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A Feasibility Report contains the basic requirements needed to determine whether a project pencils, available instantly. A Comprehensive Report contains every requirement across design, engineering, permitting, interconnection, and inspection, stays live for 2 years, and updates automatically as rules change.',
        },
      },
      {
        '@type': 'Question',
        name: 'How many documents does Fordje read per jurisdiction?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Fordje structures requirements from the 80 to 250+ documents that govern a single jurisdiction, including code, local checklists, permit and inspection requirements, utility rules, engineering standards, and contact information.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can Fordje integrate with our existing software?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Fordje data is available through the web app, CSV and PDF exports, and an API that connects into CRM and project management software.',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Product — Fordje</title>
        <meta
          name="description"
          content="Fordje gives solar, storage, and EV teams every local regulation and utility requirement for a jurisdiction, from feasibility through inspection."
        />
        <link rel="canonical" href="https://www.fordje.com/product" />
        <meta property="og:title" content="Product — Fordje" />
        <meta
          property="og:description"
          content="Fordje gives solar, storage, and EV teams every local regulation and utility requirement for a jurisdiction, from feasibility through inspection."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fordje.com/product" />
        <meta property="og:image" content="https://www.fordje.com/og-image.png" />
        <meta property="og:site_name" content="Fordje" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Product — Fordje" />
        <meta
          name="twitter:description"
          content="Fordje gives solar, storage, and EV teams every local regulation and utility requirement for a jurisdiction, from feasibility through inspection."
        />
        <meta name="twitter:image" content="https://www.fordje.com/og-image.png" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqData)}</script>
      </Helmet>

      <Header variant="light" />
      <main>

      {/* ─────────── Hero ─────────── */}
      <section className="relative overflow-hidden bg-fordje-navy-dark">
        {/* Subtle blue glow behind the headline */}
        <div className="absolute top-[-10%] right-[-10%] w-[55%] h-[55%] bg-fordje-blue/15 rounded-full blur-[120px] pointer-events-none" />

        <div className="container-custom relative z-10 pt-28 md:pt-36 pb-10 md:pb-14">
          <div className="max-w-3xl mx-auto text-center">

            <p className="text-[15px] uppercase tracking-widest text-fordje-blue font-medium mb-4 animate-fade-in">
              The Product
            </p>
            <h1 className="text-white mb-6 animate-fade-in leading-[1.1] text-4xl md:text-5xl lg:text-[3.25rem]">
              Every requirement for your project, and{' '}
              <span className="gradient-text">every change that affects it.</span>
            </h1>
            <p
              className="text-base md:text-lg text-fordje-gray mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in"
              style={{ animationDelay: '0.1s' }}
            >
              Clean energy projects face expensive delays for requirements missed at prospecting or design.
              Solar, storage, and EV teams need every requirement before the first drawing, not
              after the reviewer rejects it.
            </p>
            <div className="animate-fade-in" style={{ animationDelay: '0.15s' }}>
              <Link to="/request-demo">
                <Button
                  size="lg"
                  className="text-lg px-8 py-6 bg-gradient-to-r from-fordje-blue to-fordje-accent hover:from-fordje-blue-dark hover:to-fordje-blue shadow-lg shadow-fordje-blue/20"
                >
                  Book a Demo <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      <CustomerLogos className="border-t border-white/5" />

      {/* ─────────── What you get ─────────── */}
      <section className="pt-12 md:pt-16 pb-20 md:pb-28 bg-background border-t border-border">

        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl">
              <p className="text-[15px] uppercase tracking-widest text-primary font-medium mb-4">
                What you get
              </p>
              <h2 className="mb-6">
                A live requirements record for{' '}
                <span className="gradient-text">every project.</span>
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Feasibility through inspection, every jurisdiction's requirements are mapped,
                cited, and kept current in one place.
              </p>
            </div>

            <div className="mt-12 rounded-2xl border bg-card p-3 md:p-4 shadow-xl">
              <div className="rounded-xl overflow-hidden border bg-fordje-navy-dark">
                <img
                  src={reportDashboard}
                  alt="Comprehensive Fordje report for Atlanta, GA showing design, engineering, zoning, permitting, and interconnection requirements with citations."
                  className="w-full h-auto object-bottom"
                  loading="lazy"
                />
              </div>
              <div className="mt-4 px-2 md:px-3 flex flex-col md:flex-row md:items-center justify-between gap-3 text-base text-muted-foreground">
                <span>One project, every requirement, all cited.</span>
                <span className="font-medium text-foreground">Atlanta, GA — Comprehensive Report</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── Choose your depth (tiered) ─────────── */}
      <section className="py-20 md:py-28 bg-fordje-navy text-white border-t border-white/5">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl mb-14">
              <p className="text-[15px] uppercase tracking-widest text-fordje-blue font-medium mb-4">
                Choose your depth
              </p>
              <h2 className="text-white">
                Start light, go deep, <span className="gradient-text">stay current.</span>
              </h2>
            </div>

            <div className="grid lg:grid-cols-12 gap-6 items-start">
              {/* Feasibility — smaller, on-ramp */}
              <div className="lg:col-span-5 rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10 h-full animate-fade-in">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-fordje-gray mb-6">
                  <Zap className="h-3.5 w-3.5" />
                  The on-ramp
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold mb-4 text-white">
                  Feasibility Report
                </h3>
                <p className="text-fordje-gray leading-relaxed">
                  The basic requirements you need to determine whether a project pencils.
                  Available instantly, so you can validate the proposal while you build it.
                </p>
              </div>

              {/* Comprehensive — flagship */}
              <div className="lg:col-span-7 rounded-2xl border border-fordje-blue/40 bg-gradient-to-br from-fordje-blue/[0.08] to-white/[0.02] p-8 md:p-10 h-full shadow-lg shadow-fordje-blue/5 animate-fade-in" style={{ animationDelay: '0.08s' }}>
                <div className="inline-flex items-center gap-2 rounded-full bg-fordje-blue/15 px-3 py-1 text-xs uppercase tracking-widest text-fordje-blue mb-6">
                  <FileSearch className="h-3.5 w-3.5" />
                  The flagship
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold mb-4 text-white">
                  Comprehensive Report
                </h3>
                <p className="text-fordje-gray leading-relaxed mb-6">
                  Every requirement that applies across design, engineering, permitting,
                  interconnection, and inspection. Generate it instantly, and it stays current for
                  2 years.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-white/90">
                    <CheckCircle2 className="h-5 w-5 text-fordje-blue flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="font-semibold">Deep Research</strong> for project-specific
                      conditions and edge cases.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-white/90">
                    <CheckCircle2 className="h-5 w-5 text-fordje-blue flex-shrink-0 mt-0.5" />
                    <span>Stays live for 2 years — refreshed automatically as rules change.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── The data behind every project + Always current ─────────── */}
      <section className="py-16 md:py-20 bg-background border-t border-border">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Data block */}
            <div className="rounded-2xl border bg-card p-6 md:p-8 flex flex-col h-full">
              <p className="text-[15px] uppercase tracking-widest text-primary font-medium mb-4">
                The Data
              </p>
              <h2 className="text-2xl md:text-3xl mb-4">
                The data behind <span className="gradient-text">every project.</span>
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-4">
                Fordje structures requirements from the 80 to 250+ documents that govern a
                jurisdiction. Code, local checklists, permit and inspection requirements, utility
                rules, engineering standards, and contact information.
              </p>
              <p className="text-base text-foreground font-medium leading-relaxed mb-6">
                Every requirement is mapped to the project and cited to its source.
              </p>
              <div className="mt-auto">
                <div className="text-4xl md:text-5xl font-bold gradient-text leading-none mb-2">
                  80–250+
                </div>
                <p className="text-sm text-muted-foreground">
                  documents per jurisdiction, structured into a single project record.
                </p>
              </div>
            </div>

            {/* Always current block */}
            <div className="rounded-2xl border bg-card p-6 md:p-8 flex flex-col h-full">
              <p className="text-[15px] uppercase tracking-widest text-primary font-medium mb-4">
                Change Tracking
              </p>
              <h2 className="text-2xl md:text-3xl mb-4">
                Always current. <span className="gradient-text">Get ahead.</span>
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="rounded-md bg-primary/10 p-2 flex-shrink-0">
                    <BellRing className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1 text-foreground">Always current.</h3>
                    <p className="text-base text-muted-foreground leading-relaxed">
                      Every data point in a Comprehensive Report stays under watch. When one
                      changes, Fordje alerts you so you find out on your schedule, not the
                      reviewer's.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="rounded-md bg-primary/10 p-2 flex-shrink-0">
                    <TrendingUp className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1 text-foreground">Get ahead of changes.</h3>
                    <p className="text-base text-muted-foreground leading-relaxed">
                      We track approved changes on the state and federal level so you can make
                      plans ahead of time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-auto">
                <p className="text-base text-foreground font-medium leading-relaxed">
                  Plans stay valid because the data stays live.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── Where it lives (Access) ─────────── */}
      <section className="py-12 md:py-16 bg-background border-t border-border">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-[15px] uppercase tracking-widest text-primary font-medium mb-4">
              Access
            </p>
            <h2 className="mb-6">Where it lives.</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-14 leading-relaxed">
              Explore requirements in the web app and share via CSV or PDF. Or connect via API
              into your CRM or project software.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-14">
              {[
                { icon: Globe, title: 'Web app', desc: 'Explore in the browser' },
                { icon: Download, title: 'CSV / PDF', desc: 'Share with your team' },
                { icon: Plug, title: 'API', desc: 'Into your CRM, project software, or your own product' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={i}
                    className="rounded-2xl border bg-card p-8 hover:shadow-md transition-shadow animate-fade-in"
                    style={{ animationDelay: `${0.05 + i * 0.06}s` }}
                  >
                    <div className="rounded-lg bg-primary/10 p-3 inline-flex mb-4">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-foreground">{item.title}</h3>
                    <p className="text-base text-muted-foreground">{item.desc}</p>
                  </div>
                );
              })}
            </div>

            <div id="build-with-fordje" className="relative rounded-2xl border border-border bg-card overflow-hidden shadow-xl mb-14 scroll-mt-24">
              {/* Decorative glows */}
              <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-[100px] pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-fordje-accent/5 blur-[100px] pointer-events-none" />

              <div className="relative flex flex-col lg:flex-row items-center gap-10 lg:gap-12 p-8 md:p-12">
                {/* Content side */}
                <div className="flex-1 space-y-8 text-left">
                  <div className="space-y-4">
                    <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded-full border border-primary/20 bg-primary/10 text-primary">
                      API Integration
                    </span>
                    <h3 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
                      Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-fordje-blue to-fordje-accent">Fordje data</span> into your product
                    </h3>
                  </div>

                  <div className="space-y-6 text-muted-foreground leading-relaxed">
                    <p className="text-lg">
                      Your users expect the requirements inside your product to be current.
                      Keeping 28,000+ jurisdictions up to date takes a dedicated research team,
                      and every outdated requirement becomes a support ticket or a rejected
                      submittal for your customer.
                    </p>
                    <p className="text-lg border-l-2 border-primary/30 pl-6">
                      Fordje's API delivers <span className="text-foreground font-medium">structured, cited requirements</span> for
                      every jurisdiction your users work in. When a requirement changes, Fordje
                      alerts you so your product updates before your users find out the hard way.
                    </p>
                  </div>

                  <div>
                    <a
                      href="https://docs.fordje.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-primary font-medium hover:text-fordje-blue-light transition-colors"
                    >
                      <BookOpen className="h-4 w-4" />
                      View the API docs
                      <ExternalLink className="h-3.5 w-3.5 opacity-60" />
                    </a>
                  </div>
                </div>

                {/* Visual side: API response window */}
                <div className="flex-1 w-full lg:max-w-md">
                  <div className="rounded-xl border border-border bg-background/80 shadow-inner overflow-hidden text-left">
                    <div className="flex items-center gap-1.5 px-4 py-3 border-b border-border bg-muted/30">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-500/50" />
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/50" />
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                        GET /v1/requirements
                      </span>
                    </div>
                    <div className="p-6 font-mono text-sm leading-6">
                      <div><span className="text-primary">{'{'}</span></div>
                      <div className="pl-4"><span className="text-fordje-blue-light">"jurisdiction"</span><span className="text-muted-foreground">:</span> <span className="text-emerald-400">"Antioch, CA"</span><span className="text-muted-foreground">,</span></div>
                      <div className="pl-4"><span className="text-fordje-blue-light">"status"</span><span className="text-muted-foreground">:</span> <span className="text-emerald-400">"current"</span><span className="text-muted-foreground">,</span></div>
                      <div className="pl-4"><span className="text-fordje-blue-light">"citation"</span><span className="text-muted-foreground">:</span> <span className="text-emerald-400">"Antioch Muni. Code §15.68"</span><span className="text-muted-foreground">,</span></div>
                      <div className="pl-4"><span className="text-fordje-blue-light">"permit_fee_cap"</span><span className="text-muted-foreground">:</span> <span className="text-amber-200">450</span><span className="text-muted-foreground">,</span></div>
                      <div className="pl-4"><span className="text-fordje-blue-light">"last_verified"</span><span className="text-muted-foreground">:</span> <span className="text-muted-foreground">"2026-09-30"</span></div>
                      <div><span className="text-primary">{'}'}</span></div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live across 28,000+ jurisdictions
                  </div>
                </div>
              </div>
            </div>

            <Link to="/request-demo">
              <Button
                size="lg"
                className="text-lg px-8 py-6 bg-gradient-to-r from-fordje-blue to-fordje-accent hover:from-fordje-blue-dark hover:to-fordje-blue shadow-lg shadow-fordje-blue/20"
              >
                Book a Demo <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default Product;
